/*
 * Copyright (c) 2008-2021 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import { uuid } from "vue-uuid"
import { log } from "~/common/src/utils/log"
import api from "@/config/api"
import Vue from "vue"
import communication from "~/common/src/messaging" // or simply 'messaging' (but this is not detected by IDEA)
import { EventBus } from "~/common/src/event-bus"
import { INTERACTION_RESET_FINISHED } from "@/config/events"

// enum for subscription scopes
export const subscriptionScope = {
  NOTIFICATION: "NOTIFICATION",
  CONTRIBUTION_DATA: "CONTRIBUTION_DATA",
}
// makes the const more like an enum
// see https://stackoverflow.com/questions/287903/what-is-the-preferred-syntax-for-defining-enums-in-javascript
Object.freeze(subscriptionScope)

// Mapping tool type to toolModel type
export const oidTypes = {
  text: "TextToolModel",
  qa: "TextToolModel",
  vote: "VoteToolModel",
  starvote: "VoteToolModel",
  hotornot: "HotOrNotToolModel",
  opinionpoll: "VoteToolModel",
  message: "MessageToolModel",
  imagevote: "VoteToolModel",
  category: "VoteToolModel",
  numeric: "NumericToolModel",
  xymatrix: "XYMatrixToolModel",
  election: "ElectionToolModel",
  "person-election": "ElectionToolModel",
  "list-election": "ElectionToolModel",
}

function doResetMeeting(store, state, meetingId) {
  log.info("Removing contributions of meeting: ", meetingId)
  let interactions = store.getters["meetings/interactions"](meetingId)

  interactions.forEach((interaction) => {
    doResetInteraction(state, interaction)
  })
}

function doResetInteraction(state, interactionOrMessage) {
  let type, interactionId
  if (interactionOrMessage.type && interactionOrMessage.interactionId) {
    // non-OID-type tool-id
    type = oidTypes[interactionOrMessage.type]
    if (!type) {
      return
    }
    interactionId = type + "_" + interactionOrMessage.interactionId
  } else if (interactionOrMessage.toolId) {
    // OID-type tool id
    let separator = interactionOrMessage.toolId.indexOf("_")
    type = interactionOrMessage.toolId.substring(0, separator)
    interactionId = interactionOrMessage.toolId
  } else {
    log.error("Unknown message format: " + interactionOrMessage)
  }

  if (!interactionOrMessage.toolResetConfig) {
    return
  }

  switch (type) {
    case "TextToolModel":
      doResetTextInteraction(state, interactionOrMessage.toolResetConfig, interactionId)
      break
    default:
      // VoteToolModel, NumericToolModel etc. pretty much all work the same in this respect
      doResetVoteInteraction(state, interactionOrMessage.toolResetConfig, interactionId)
      break
  }

  // Trigger reset finished event so components can reset themselves, if necessary
  EventBus.safeTrigger(INTERACTION_RESET_FINISHED, { interactionId: interactionId, type: type })
}

function doResetTextInteraction(state, toolResetConfig, interactionId) {
  log.info("Removing contributions of text interaction", interactionId)
  let contribIds = state.contributionMap[interactionId]

  let deleteUnclusteredContributions = toolResetConfig.deleteUnclusteredContributions
  let deleteClusteredContributions = toolResetConfig.deleteClusteredContributions
  let deleteClusters = toolResetConfig.deleteClusters

  let unclusteredContributionIds = []
  let clusteredContributionIds = []
  let clusterIds = []

  if (contribIds && contribIds.length > 0) {
    unclusteredContributionIds = contribIds.filter((contribId) => {
      let contribution = state.contributions[contribId]
      return contribution.contributionType !== "CLUSTER" && contribution.parents.length === 0
    })

    clusteredContributionIds = contribIds.filter((contribId) => {
      let contribution = state.contributions[contribId]
      return contribution.contributionType !== "CLUSTER" && contribution.parents.length > 0
    })

    clusterIds = contribIds.filter((contribId) => {
      let contribution = state.contributions[contribId]
      return contribution.contributionType === "CLUSTER"
    })
  } // else: they haven't been fetched as we didn't need them yet

  if (deleteClusters || deleteClusteredContributions) {
    // If we are deleting clusters, we first need to orphan all their children (sounds rather dark, but that's life)
    // If we are deleting clustered contributions, same thing - remove them first, can't kill kids with parents present
    clusteredContributionIds.forEach((contributionId) => {
      let contribution = state.contributions[contributionId]
      contribution.parents.forEach((parentId) => {
        let parent = state.contributions[parentId]
        parent.children.splice(parent.children.indexOf(contributionId), 1)
      })

      contribution.parents = []
    })
  }

  // Now we can delete everything that was selected. Note that if we chose to delete unclustered contributions and clusters,
  // but not clustered contributions, the result will be that clustered contributions become unclustered, but stay
  // alive, while previously unclustered contributions get removed.
  let contribIdsToDelete = []
  if (deleteUnclusteredContributions) {
    contribIdsToDelete.push(...unclusteredContributionIds)
  }
  if (deleteClusteredContributions) {
    contribIdsToDelete.push(...clusteredContributionIds) // To be precise, formerly clustered contributions at this point
  }
  if (deleteClusters) {
    contribIdsToDelete.push(...clusterIds)
  }

  log.debug("Deleting contributions", contribIdsToDelete)
  contribIdsToDelete.forEach((id) => {
    Vue.delete(state.contributions, id)

    // Remove from contributionMap inner array
    let index = state.contributionMap[interactionId].indexOf(id)
    Vue.delete(state.contributionMap[interactionId], index)
  })

  // Need to recalculate contribution counts. Fortunately, for text tools, weighted and unweighted counts are the same
  let newContributionCount = (state.contributionMap[interactionId] ?? [])
    .map((contribId) => state.contributions[contribId])
    .filter((contribution) => contribution.contributionType !== "CLUSTER").length // These don't count
  Vue.set(state.contributionCounts, interactionId, newContributionCount)
  Vue.set(state.weightedContributionCounts, interactionId, newContributionCount)
}

function doResetVoteInteraction(state, toolResetConfig, interactionId) {
  log.info("Removing contributions of vote interaction", interactionId)
  let contribIds = state.contributionMap[interactionId]

  let deleteVoteContributions = toolResetConfig.deleteVoteContributions
  if (!deleteVoteContributions) {
    return // Nothing to do.
  }

  // We don't care about this flag here. deleting row ids is done on the server and we receive news about it
  // through a different channel
  // let deleteRowIds = toolResetConfig.deleteRowIds

  if (contribIds && contribIds.length > 0) {
    let contribIdsToDelete = [...contribIds] // Let's work on a copy here, otherwise weird things start to happen
    log.debug("Deleting contributions", contribIdsToDelete)
    contribIdsToDelete.forEach((id) => {
      Vue.delete(state.contributions, id)

      // Remove from contributionMap inner array
      let index = state.contributionMap[interactionId].indexOf(id)
      Vue.delete(state.contributionMap[interactionId], index)
    })
  } // else: Contributions haven't been fetched

  // Set contribution counts to 0. Now, that was easy!
  Vue.set(state.contributionCounts, interactionId, 0)
  Vue.set(state.weightedContributionCounts, interactionId, 0)
}

export default {
  namespaced: true,

  state: {
    // Mapping from interactionId to number of contributions (including not yet loaded contributions) (int -> int)
    contributionCounts: {},
    weightedContributionCounts: {},
    // Mapping from interactionId to list of (loaded) contributionIds (String -> String[])
    contributionMap: {},
    // Mapping from contributionId to contribution (String -> Object)
    contributions: {},
    // Status indicator; mapping from interactionId to boolean
    hasLoaded: {},
  },

  mutations: {
    notificationReceived(state, message) {
      let contributionCounts = message.contributionCounts
      for (let interactionId in contributionCounts) {
        if (contributionCounts.hasOwnProperty(interactionId)) {
          let count = contributionCounts[interactionId]
          log.info("Updating number of contributions of tool", interactionId, "to", count)
          Vue.set(state.contributionCounts, interactionId, count)
        }
      }
      let weightedContributionCounts = message.weightedContributionCounts
      for (let interactionId in weightedContributionCounts) {
        if (weightedContributionCounts.hasOwnProperty(interactionId)) {
          let count = weightedContributionCounts[interactionId]
          log.info("Updating weighted number of contributions of tool", interactionId, "to", count)
          Vue.set(state.weightedContributionCounts, interactionId, count)
        }
      }
    },

    /**
     * Store text contributions to state (map of interactionId to list of contributions)
     *
     * @param state
     * @param message
     */
    storeContributions(state, message) {
      let interactionId = message.toolId
      Vue.set(state.hasLoaded, interactionId, true)

      let contributions = message.contributions
      if (!contributions) {
        // No contributions
        contributions = {}
      }

      // Append newContributionIds to existing contributionIds after removing duplicates
      // (see https://stackoverflow.com/a/23080662/5515410)
      let existing = state.contributionMap[interactionId]
      if (!existing) {
        existing = []
      }

      // Workaround for ugly server-side Json serializer:
      // We have to replace nulls which (are serialized as empty strings) by empty arrays
      // pending: as soon as the old client handles nulls in JSON everywhere correctly remove this workaround again
      contributions.forEach((c) => {
        if ("" === c.children) {
          c.children = []
        }
        if ("" === c.parents) {
          c.parents = []
        }
      })

      let newContributionIds = contributions.map((c) => c.id)
      let reallyNewContributionIds = newContributionIds.filter((item) => existing.indexOf(item) < 0)
      if (reallyNewContributionIds.length > 0) {
        log.info("Appending ", reallyNewContributionIds.length, " new contributions of interaction", interactionId)
        Vue.set(state.contributionMap, interactionId, existing.concat(reallyNewContributionIds))
      }

      // Ensure store contains any updates of the contributions
      contributions.forEach((contribution) => {
        // Set a parent node to allow a look-back up the tree
        contribution.parent = interactionId

        Vue.set(state.contributions, contribution.id, contribution)
      })

      // Process property updates for existing contributions
      let propertyUpdates = message.propertyUpdates || {}
      // Iterate over contributions for that updates are available
      for (let contributionId in propertyUpdates) {
        // Ignore contributions not in the store
        if (!state.contributions.hasOwnProperty(contributionId)) {
          log.debug("Skipping update for contribution not contained in store. - contributionId: ", contributionId)
          continue
        }
        let contribution = state.contributions[contributionId]
        // Iterate over individual updates
        propertyUpdates[contributionId].forEach((update) => {
          if (update.hasOwnProperty("messageType")) {
            let propertyName = update.propertyName
            log.info("Updating property ", propertyName, " at contribution ", contributionId)

            if (update.messageType === "AssignProperty") {
              Vue.set(contribution, propertyName, update.value)
            } else if (update.messageType === "MapPut") {
              if (contribution.hasOwnProperty(propertyName)) {
                Vue.set(contribution[propertyName], update.key, update.value)
              } else {
                Vue.set(contribution, propertyName, { [update.key]: update.value })
              }
            } else if (update.messageType === "ListAdd") {
              // Check if property exists and value to be removed is contained in array
              if (contribution.hasOwnProperty(propertyName)) {
                if (!contribution[propertyName].includes(update.value)) {
                  contribution[propertyName].splice(update.index, 0, update.value)
                  Vue.set(contribution, propertyName, contribution[propertyName])
                } // else: update already committed
              } else {
                // Should never happen (may later lead to problems if the index is wrong...)
                log.warn("Weird - no property '", propertyName, " defined on object: ", contributionId)
                Vue.set(contribution, propertyName, [update.value])
              }
            } else if (update.messageType === "ListRemove") {
              // Check if property exists and value to be removed is contained in array
              if (contribution.hasOwnProperty(propertyName)) {
                if (contribution[propertyName].includes(update.removedValue)) {
                  contribution[propertyName].splice(update.index, 1)
                  Vue.set(contribution, propertyName, contribution[propertyName])
                } // else: update already committed
              } else {
                log.warn("Weird - no property '", propertyName, " defined on object: ", contributionId)
              }
            } else if (update.messageType === "ListMove") {
              if (contribution.hasOwnProperty(propertyName)) {
                if (contribution[propertyName][update.to] !== update.movedValue) {
                  // Stolen from https://stackoverflow.com/a/7180095/5515410
                  contribution[propertyName].splice(update.to, 0, contribution[propertyName].splice(update.from, 1)[0])
                  Vue.set(contribution, propertyName, contribution[propertyName])
                } // else: update already applied
              } // else: nothing to do
            } else {
              log.warn(
                "Skipping update because of unknown message type ",
                update.messageType,
                " at contribution ",
                contributionId
              )
            }
          }
        })
      }
    },

    setOnStage(state, message) {
      let contributionId = message.contributionId

      let contribution = state.contributions[contributionId]
      if (!contribution) {
        log.info("Contribution not available: ", contributionId)
        return
      }

      let onStageValue = message.onStage
      if (!onStageValue) {
        onStageValue = null
      }

      log.info("Setting onStage tag to ", onStageValue, " for contribution", contributionId)
      Vue.set(contribution.tags, "onStage", onStageValue)
    },

    clearStage(state, interactionId) {
      log.info("Setting onStage tag to 'WAS' for currently displayed contributions of interactionId: ", interactionId)
      let contributionIds = state.contributionMap[interactionId]
      if (!contributionIds) {
        return
      }
      let contributions = contributionIds.map((id) => state.contributions[id])
      let onStageContributions = contributions.filter((c) => c.tags && c.tags.onStage === "IS")
      onStageContributions.forEach((c) => {
        Vue.set(c.tags, "onStage", "WAS")
      })
    },

    resetInteraction(state, message) {
      doResetInteraction(state, message)
    },

    resetMeeting(state, { meetingId }) {
      // Reset specified meeting
      doResetMeeting(this, state, meetingId)
    },

    setApproved(state, message) {
      let contributionId = message.contributionId

      let contribution = state.contributions[contributionId]
      if (!contribution) {
        log.info("Contribution not available: ", contributionId)
        return
      }

      let approvedValue = message.approved

      log.info("Setting approved tag to ", approvedValue, " for contribution", contributionId)
      Vue.set(contribution.tags, "approved", approvedValue)
    },

    setHiddenFlag(state, message) {
      let contributionId = message.contributionId

      let contribution = state.contributions[contributionId]
      if (!contribution) {
        log.info("Contribution not available: ", contributionId)
        return
      }

      let hideValue = !!message.hidden

      log.info("Setting hide flag to ", hideValue, " for contribution ", contributionId)

      contribution.hidden = hideValue
    },

    // Method for adding a contribution
    addContribution: function (state, { interactionId, contribution }) {
      let contributionIds = state.contributionMap[interactionId]
      if (!contributionIds) {
        Vue.set(state.contributionMap, interactionId, [])
        contributionIds = []
      }

      let contributionExists = state.contributions.hasOwnProperty(contribution.id)
      if (!contributionExists) {
        log.debug("Inserting contribution", contribution.id, ":" + JSON.stringify(contribution))
        state.contributionMap[interactionId].push(contribution.id)

        if (contribution.parents.length !== 0) {
          let parentId = contribution.parents[0]
          let parent = state.contributions[parentId]
          // Since we check if the incoming contribution already exists might as well
          // also check if the parent is already linked to the inserted contribution
          // (i.e might attempt to insert duplicate child)
          if (!parent.children.includes(contribution.id)) parent.children.push(contribution.id)
        }
        Vue.set(state.contributions, contribution.id, contribution)
      } else {
        log.warn("Trying to add new contribution that already exists in store:", JSON.stringify(contribution))
      }
    },

    // Method for editing a contribution's summary
    updateContribution: function (state, { contributionId, summary, plainSummary }) {
      if (state.contributions.hasOwnProperty(contributionId)) {
        log.debug("Updating contribution:", contributionId)

        let contribution = state.contributions[contributionId]
        contribution.summary = summary
        contribution.plainSummary = plainSummary
        // Vue.set(contribution, "summary", realSummary)
        // Vue.set(contribution, "plainSummary", plainSummary)
        Vue.set(state.contributions, contribution.id, contribution)
      } else {
        log.warn("Trying to update unknown contribution:", JSON.stringify(contributionId))
      }
    },

    hideContribution: function (state, { contributionId }) {
      log.debug("Marking contribution as hidden:", contributionId)

      let contribution = state.contributions[contributionId]
      if ("CLUSTER" === contribution.contributionType) {
        log.debug("Deleting cluster - removing parent of its children")
        // Remove parent relation from child contributions of the contribution
        contribution.children.forEach((childId) => {
          let child = state.contributions[childId]
          child.parents.splice(child.parents.indexOf(contributionId), 1)
        })
        // Empty the set of children of the contribution
        contribution.children = []
      } else {
        // Remove child relation from parent contributions of the contribution
        contribution.parents.forEach((parentId) => {
          let parent = state.contributions[parentId]
          // Remove child from each parent
          parent.children.splice(parent.children.indexOf(contributionId), 1)
        })
        // Empty the set of parents of the contribution
        contribution.parents = []
      }

      // Hide contribution itself
      Vue.set(contribution, "hidden", true)
    },

    // Method for changing the color of a cluster
    changeClusterColor: function (state, { clusterId, color }) {
      log.debug("Changing color of cluster contribution: ", clusterId)
      let cluster = state.contributions[clusterId]
      if (cluster) {
        cluster.unselectedColor = color
      }
    },

    // Make a contribution an orphan and remove the id from parents
    removeContributionParents: function (state, { contributionId }) {
      // TODO: Remove only one parent when multi parents is implemented
      log.debug("Removing parents of contribution: ", contributionId)
      let c = state.contributions[contributionId]

      // Remove child from each parent
      c.parents.forEach((parentId) => {
        let parent = state.contributions[parentId]
        parent.children.splice(parent.children.indexOf(contributionId), 1)
      })

      c.parents = []
    },

    // Set the parent of a contribution and erase from oldParent children
    editContributionParent: function (state, { contributionId, parentId }) {
      log.debug("Setting new parent of contribution: ", contributionId, ": ", parentId)
      let c = state.contributions[contributionId]

      // Remove child from each parent
      c.parents.forEach((oldParentId) => {
        let parent = state.contributions[oldParentId]
        parent.children.splice(parent.children.indexOf(contributionId), 1)
      })

      // Add child relation to the new parent
      let parent = state.contributions[parentId]
      parent.children.push(contributionId)

      // Set relation to specified parent
      c.parents = [parentId]
    },

    // Method for moving the children of a contribution, for ordering
    moveContributionToIndex: function (state, { parentId, childId, index }) {
      log.debug("Moving contribution inside of a cluster to a new index:", childId, "/ index", index)
      let parent = state.contributions[parentId]
      parent.children.splice(parent.children.indexOf(childId), 1)
      parent.children.splice(index, 0, childId)
    },
  },

  actions: {
    /**
     * Subscribes/ unsubscribes for receiving notifications on contributions of the specified interaction.
     * (Client will be unsubscribed from all subscription scopes not contained in the passed scopes array.)
     *
     * @param commit
     * @param state
     * @param interactionId
     * @param scopes The scopes to subscribe to (previous scopes will be unsubscribed)
     */
    // eslint-disable-next-line no-empty-pattern
    subscribeForContributions({}, { interactionId, scopes }) {
      log.info(
        "Adding or updating subscription for interaction ",
        interactionId,
        " to use subscription scopes ",
        scopes
      )
      communication.sendMessage({
        messageType: "ClientSubscriptionMessage",
        toolId: interactionId,
        subscriptionScopes: scopes,
      })
    },

    /**
     * Load contributions from server
     */
    requestContributions(_, { interactionId }) {
      log.info("Loading contributions from server:", interactionId)

      communication.sendMessage({
        messageType: "RequestContributionsMessage",
        toolId: interactionId,
      })
    },

    setOnStage({ state, commit }, { contributionId, newValue }) {
      log.info("Going to set onStage tag to", newValue, " of contribution", contributionId)

      // single mode - anticipate server behavior and clear stage before putting new contribution on
      if (newValue === "IS") {
        let contribution = state.contributions[contributionId]
        let interactionId = contribution.parent
        commit("clearStage", interactionId)
      }
      commit("setOnStage", { contributionId, onStage: newValue })

      communication.sendMessage({
        messageType: "SetOnStageMessage",
        contributionId: contributionId,
        onStage: newValue,
      })
    },

    setApproved({ commit }, { contributionId, newValue }) {
      log.info("Going to set approved tag to ", newValue, " of contribution ", contributionId)

      commit("setApproved", { contributionId, approved: newValue })

      communication.sendMessage({
        messageType: "SetApprovedMessage",
        contributionId: contributionId,
        approved: newValue,
      })
    },

    setHiddenFlag({ commit }, { contributionId, newValue }) {
      log.info("Going to set hide flag to ", newValue, " of contribution ", contributionId)

      commit("setHiddenFlag", { contributionId, hidden: newValue })

      communication.sendMessage({
        messageType: "SetHiddenStateMessage",
        contributionId: contributionId,
        hidden: newValue,
      })
    },

    clearStage({ commit }, { interactionId }) {
      commit("clearStage", interactionId)

      communication.sendMessage({
        messageType: "ClearStageMessage",
        toolId: interactionId,
      })
    },

    resetInteraction({ commit }, { interaction }) {
      if (!oidTypes[interaction.type]) {
        // Paranoia check... Should never happen
        log.error("Unknown interaction type: " + interaction.type)
        return
      }
      commit("resetInteraction", {
        type: interaction.type,
        interactionId: interaction.interactionId,
        toolResetConfig: interaction.toolResetConfig,
      })
      communication.sendMessage({
        messageType: "AsynchronousResetToolRequest",
        toolId: oidTypes[interaction.type] + "_" + interaction.interactionId,
        toolResetConfig: interaction.toolResetConfig,
      })
    },

    resetMeeting({ commit }, { id }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.RESET_MEETING(id), {}).then(
          (response) => {
            commit("resetMeeting", { meetingId: id })
            resolve(response.data)
          },
          (response) => {
            log.error("Could not reset meeting, error: ", response)
            reject(response)
          }
        )
      })
    },

    addContribution: function ({ commit }, { interactionId, contribution }) {
      if (contribution.summary && !contribution.summary.startsWith("<p")) {
        contribution.summary = "<p>" + contribution.summary + "</p>"
      }
      commit("addContribution", { interactionId, contribution })

      communication.sendMessage({
        messageType: "InsertContributionMessage",
        interactionId: interactionId,
        contribution: contribution,
      })
    },

    updateContribution: function ({ commit }, { interactionId, contributionId, summary }) {
      let plainSummary = summary
      let realSummary = summary
      if (realSummary && !realSummary.startsWith("<p")) {
        realSummary = "<p>" + realSummary + "</p>"
      }
      commit("updateContribution", { contributionId, summary: realSummary, plainSummary })

      communication.sendMessage({
        messageType: "UpdateContributionMessage",
        interactionId: interactionId,
        contributionId: contributionId,
        summary: realSummary,
      })
    },

    hideContribution: function ({ commit }, { contributionId }) {
      commit("hideContribution", {
        contributionId: contributionId,
      })

      communication.sendMessage({
        messageType: "RemoveContributionMessage",
        contributionId: contributionId,
      })
    },

    changeClusterColor: function ({ commit }, { clusterId, color }) {
      commit("changeClusterColor", {
        clusterId: clusterId,
        color: color,
      })
      communication.sendMessage({
        messageType: "SetContributionColorMessage",
        contributionId: clusterId,
        color: color,
      })
    },

    removeContributionParents: function ({ commit }, { contributionId }) {
      commit("removeContributionParents", { contributionId })

      communication.sendMessage({
        messageType: "SetContributionParentsMessage",
        contributionId: contributionId,
        parents: [],
      })
    },

    editContributionParent: function ({ commit }, { contributionId, parentId }) {
      commit("editContributionParent", {
        contributionId: contributionId,
        parentId: parentId,
      })

      communication.sendMessage({
        messageType: "SetContributionParentsMessage",
        contributionId: contributionId,
        parents: [parentId],
      })
    },

    cloneContribution: function ({ commit }, { contribution, interactionId }) {
      log.info(
        "Cloning contribution ",
        contribution.id,
        ":" + JSON.stringify(contribution),
        " for interaction ",
        interactionId
      )

      // create the new cloned contribution with a new id
      let clonedContribution = JSON.parse(JSON.stringify(contribution))

      clonedContribution.id = "TextContribution_" + uuid.v4()
      clonedContribution.displayId = null

      // Insert the cloned contribution to the list of contribution
      log.info("Inserting temporary the cloned contribution ", clonedContribution.id)
      commit("addContribution", {
        interactionId: interactionId,
        contribution: clonedContribution,
      })

      // notify the server about the cloned contribution
      communication.sendMessage({
        messageType: "InsertContributionMessage",
        interactionId: interactionId,
        contribution: clonedContribution,
      })
    },

    moveContributionToIndex: function ({ commit }, { parentId, childId, index }) {
      commit("moveContributionToIndex", {
        parentId: parentId,
        childId: childId,
        index: index,
      })
      communication.sendMessage({
        messageType: "MoveContributionToIndexMessage",
        parentId: parentId,
        childId: childId,
        index: index,
      })
    },
  },

  getters: {
    // --------------------------------- Generic getters ---------------------------------

    hasLoaded: (state) => (interactionId) => {
      let loaded = state.hasLoaded[interactionId]
      return loaded ? loaded : false
    },

    contribution: (state) => (contributionId) => {
      return state.contributions[contributionId]
    },

    contributions: (state) => (interactionId) => {
      let contributionIds = state.contributionMap[interactionId]
      return contributionIds ? contributionIds.map((id) => state.contributions[id]) : []
    },

    count: (state) => (interactionId) => {
      let contributionIds = state.contributionMap[interactionId]
      return contributionIds ? contributionIds.length : 0
    },

    // --------------------------------- Cluster getters ---------------------------------

    orphanContributions: (state) => (interactionId) => {
      let contributionIds = state.contributionMap[interactionId]
      if (!contributionIds) {
        return []
      }

      return contributionIds
        .map((id) => state.contributions[id])
        .filter((c) => c.parents.length === 0 && !c.hidden && "CLUSTER" !== c.contributionType)
    },

    orphanCount: (state, getters) => (interactionId) => {
      return getters.orphanContributions(interactionId).length
    },

    clusterContributions: (state) => (interactionId) => {
      let contributionIds = state.contributionMap[interactionId]
      if (!contributionIds) {
        return []
      }

      return contributionIds
        .map((id) => state.contributions[id])
        .filter((c) => !c.hidden && "CLUSTER" === c.contributionType)
    },

    clusterCount: (state, getters) => (interactionId) => {
      return getters.clusterContributions(interactionId).length
    },

    childContributions: (state) => (interactionId, parentId) => {
      let contributionIds = state.contributionMap[interactionId]
      if (!contributionIds) {
        return []
      }

      let parent = state.contributions[parentId]
      if (!parent) {
        log.warn("Weird - requested contributions of non-existent cluster:", parentId)
        return
      }

      return parent.children.map((childId) => state.contributions[childId]).filter((c) => c && !c.hidden)
    },

    // --------------------------------- Q&A getters ---------------------------------

    isOnStage: (state) => (contributionId) => {
      let c = state.contributions[contributionId]
      if (c && c.tags) {
        let value = c.tags.onStage
        if (value === "IS") {
          return true
        }
      }
      return false
    },

    isApproved: (state) => (contributionId) => {
      let c = state.contributions[contributionId]
      if (c && c.tags) {
        let value = c.tags.approved
        if (value === "true") {
          return true
        }
      }
      return false
    },

    getOnStageValue: (state) => (contributionId) => {
      let c = state.contributions[contributionId]
      if (c && c.tags) {
        let value = c.tags.onStage
        if (value) {
          return value
        }
      }
      return null
    },

    presentedCount: (state) => (interactionId) => {
      let contributionIds = state.contributionMap[interactionId]
      if (!contributionIds) {
        return 0
      }

      let contributions = contributionIds.map((id) => state.contributions[id])
      return contributions.filter((c) => c.tags && c.tags.onStage === "IS").length
    },

    contributionCount: (state) => (interactionId) => {
      return state.contributionCounts[interactionId]
    },

    weightedContributionCount: (state) => (interactionId) => {
      return state.weightedContributionCounts[interactionId]
    },

    unloadedContributionCount: (state) => (interactionId) => {
      let total = state.contributionCounts[interactionId]

      let contributionIds = state.contributionMap[interactionId]
      let loaded = contributionIds ? contributionIds.length : 0

      // If no new contributions were added we set it to the number of contributions we loaded already
      total = total ? total : 0

      // Paranoia check to avoid negative values - as you know, counting isn't that easy at teambits...
      return Math.max(0, total - loaded)
    },
  },
}
